import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';

import { BookingViewMode } from '../court-grid.model';

interface ViewTab {
  mode: BookingViewMode;
  label: string;
}

const TABS: ViewTab[] = [
  { mode: 'simplified', label: 'Einfache Ansicht' },
  { mode: 'day', label: 'Tagesansicht' },
  { mode: 'week', label: 'Wochenansicht' },
  { mode: 'day-v2', label: 'Tagesansicht v2' },
  { mode: 'week-v2', label: 'Wochenansicht v2' },
];

/**
 * Underline-style view switcher (Einfache/Tages-/Wochenansicht + the v2
 * "Vertikale Lanes" day/week variants), per
 * the approved "Booking-Container Rework" design — matches that mockup's own
 * tabActive/tabIdle underline treatment, not the separate segmented-pill
 * exploration file.
 */
@Component({
  selector: 'app-view-toggle',
  standalone: true,
  imports: [],
  template: `
    <div class="view-toggle">
      <div class="view-toggle-tabs">
        <!-- Deliberately PLAIN toggle buttons with aria-pressed, NOT ARIA
             tabs: the switcher swaps entire page sections rendered by a
             different component (no stable panel ids for aria-controls), and
             a partial tabs pattern (role="tab" without roving tabindex /
             arrow-key nav) promises AT behavior that doesn't exist. -->
        <div class="view-toggle-tablist">
          @for (tab of tabs; track tab.mode) {
            <button
              type="button"
              class="view-tab"
              [class.active]="viewMode === tab.mode"
              [attr.aria-pressed]="viewMode === tab.mode"
              (click)="onToggle(tab.mode)">
              {{ tab.label }}
            </button>
          }
        </div>
        <!-- Optional right-aligned action slot ON the tab row itself (e.g. the
             day/week grid's expand button). Renders nothing when empty. -->
        <div class="view-toggle-actions">
          <ng-content select="[viewToggleActions]" />
        </div>
      </div>
      <!-- Optional slot BELOW the tab row, inside the same widget (e.g. the
           duration filter in Einfache Ansicht). Renders nothing when nothing
           is projected (:empty). -->
      <div class="view-toggle-aside">
        <ng-content />
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    /* Without this, the host defaults to display:inline, which lets a
       text-align:center ancestor visually center this component instead of
       letting it sit left-aligned at its natural content width. */
    :host {
      display: block;
    }

    /* Tabs on top, projected content stacked below — one widget, two rows. */
    .view-toggle {
      display: flex;
      flex-direction: column;
    }

    /* The tab row carries the full-width underline border; the active tab's
       -1px bottom margin lands its accent on this border. */
    .view-toggle-tabs {
      display: flex;
      align-items: center;
      gap: 20px;
      border-bottom: 1px solid #e2e8f0;
    }

    /* Tabs keep stretch alignment inside their own sub-row so the active
       underline still lands on the container border even when a taller
       action (e.g. the 38px expand button) shares the row. Five tabs no
       longer fit narrow cards/mobile — the tablist pans horizontally
       (scrollbar hidden; the underline border scrolls with its tab). */
    .view-toggle-tablist {
      display: flex;
      align-self: stretch;
      gap: 20px;
      min-width: 0;
      overflow-x: auto;
      scrollbar-width: none;
    }

    .view-toggle-tablist::-webkit-scrollbar {
      display: none;
    }

    @media (max-width: 768px) {
      .view-toggle-tablist {
        gap: 14px;
      }
    }

    /* Right-aligned actions ON the tab row (e.g. the grid's expand button),
       vertically centered against the tabs; slight bottom breathing room so
       a taller control doesn't sit on the underline border. */
    .view-toggle-actions {
      display: flex;
      align-items: center;
      margin-left: auto;
      padding-bottom: 4px;
    }

    .view-toggle-actions:empty {
      display: none;
    }

    /* Projected content sits below the tab row's border, left-aligned, with
       breathing room to read as a second row of the same widget. */
    .view-toggle-aside {
      display: flex;
      align-items: center;
      min-width: 0;
      padding-top: 18px;
    }

    /* Nothing projected (or an @if that renders nothing): contribute nothing
       below the tabs — no stray gap, no extra height. Angular's comment
       anchors don't prevent :empty from matching. */
    .view-toggle-aside:empty {
      display: none;
    }

    .view-tab {
      padding: 6px 1px 10px;
      border: none;
      border-bottom: 2px solid transparent;
      background: none;
      color: #94a3b8;
      font-size: 0.82rem;
      font-weight: 500;
      cursor: pointer;
      white-space: nowrap;
      font-family: inherit;
      margin-bottom: -1px;
      transition: color 0.15s ease, border-color 0.15s ease;
    }

    .view-tab:hover:not(.active) {
      color: #475569;
    }

    .view-tab.active {
      color: #334155;
      font-weight: 700;
      border-bottom-color: var(--primary-color, #045e8b);
    }
  `]
})
export class ViewToggleComponent {
  @Input() viewMode: BookingViewMode = 'simplified';
  @Output() viewModeChanged = new EventEmitter<BookingViewMode>();

  readonly tabs = TABS;

  onToggle(mode: BookingViewMode): void {
    if (mode !== this.viewMode) {
      this.viewModeChanged.emit(mode);
    }
  }
}
